import { Button, Group, Modal, ScrollArea, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { PositionRequest } from '../../../models/opportunity.model';
import { useAddPosition } from '../hooks/usePositionMutations';
import { emptyPositionRow } from '../utils/position.utils';
import { PositionEditor, PositionFormRow } from './PositionEditor';

interface PositionAddModalProps {
  opportunityId: number;
  nextSortOrder: number;
  opened: boolean;
  onClose: () => void;
}

export const PositionAddModal = ({ opportunityId, nextSortOrder, opened, onClose }: PositionAddModalProps) => {
  const [formRow, setFormRow] = useState<PositionFormRow>(() => emptyPositionRow(nextSortOrder));
  const addPosition = useAddPosition(opportunityId);

  const handleSave = () => {
    const data: PositionRequest = {
      title: formRow.title.trim(),
      description: formRow.description.trim() || null,
      headCount: formRow.headCount,
      sortOrder: formRow.sortOrder,
      technologies: formRow.technologies.map((t) => ({
        technologyCanonicalId: t.technologyCanonicalId,
        requiredLevel: t.requiredLevel,
        requiredWeight: t.requiredWeight,
      })),
      certifications: formRow.certifications.map((c) => ({
        certificateId: c.certificateId ?? null,
        certificationFamilyId: c.certificationFamilyId ?? null,
      })),
      languages: formRow.languages.map((l) => ({
        languageId: l.languageId,
        requiredProficiencyId: l.requiredProficiencyId ?? null,
        requiredWeight: l.requiredWeight,
      })),
    };
    addPosition.mutate(data, {
      onSuccess: () => {
        notifications.show({ title: 'Added', message: 'Position added.', color: 'sopraGreen' });
        onClose();
      },
      onError: () => {
        notifications.show({ title: 'Error', message: 'Failed to add position.', color: 'red' });
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Add position</Text>}
      size="xl"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="md">
        <PositionEditor
          position={formRow}
          index={0}
          onChange={setFormRow}
          onRemove={onClose}
          canRemove={false}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose} disabled={addPosition.isPending}>Cancel</Button>
          <Button variant="filled" color="brand.5" onClick={handleSave} loading={addPosition.isPending}>Add Position</Button>
        </Group>
      </Stack>
    </Modal>
  );
};
